import React, { useEffect, useState } from 'react';
import { Button, Modal, Row, Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';

const StockWastageModal = ({ show, onClose }) => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;

    const [storageRooms, setStorageRooms] = useState([]);
    const [rows, setRows] = useState([
        { room: null, rawMaterial: null, quantity: '', unit: null, rawMaterials: [], units: [] }
    ]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState([]);  // <-- added

    // Fetch storage rooms
    useEffect(() => {
        const fetchRooms = async () => {
            try {
                const res = await axios.get(`${baseURL}/storageroom/active/list`);
                const rooms = res.data.data.map(r => ({ value: r.sno, label: r.storageroom_name }));
                setStorageRooms(rooms);
            } catch (err) {
                console.error(err);
            }
        };
        fetchRooms();
    }, []);

    const fetchRawMaterials = async (roomSno, rowIndex) => {
        try {
            const res = await axios.get(`${baseURL}/raw-materials-rooms`, {
                params: { room_sno: roomSno }
            });

            const materials = res.data.data.map(rm => ({
                value: rm.sno,
                label: rm.rawmaterial_name
            }));

            setRows(prev => {
                const newRows = [...prev];
                newRows[rowIndex].rawMaterials = materials;
                newRows[rowIndex].rawMaterial = null;
                return newRows;
            });

        } catch (err) {
            console.error(err);
        }
    };


    // Fetch units
    const fetchUnits = async (rowIndex) => {
        try {
            const res = await axios.get(`${baseURL}/manageunit/by-category`, { params: { unit_category_sno: 2 } });
            const units = res.data.data.map(u => ({
                value: u.unit_name,
                label: u.unitslug_name
            }));

            setRows(prev => {
                const newRows = [...prev];
                newRows[rowIndex].units = units;
                if (rowIndex !== 0) newRows[rowIndex].unit = null;
                return newRows;
            });
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        rows.forEach((_, idx) => fetchUnits(idx));
    }, [rows.length]);

    const handleAddRow = () => {
        const firstRowUnits = rows[0].units || [];
        setRows([...rows, { room: null, rawMaterial: null, quantity: '', unit: null, rawMaterials: [], units: firstRowUnits }]);
    };

    const handleRemoveRow = (index) => {
        if (index === 0) return;
        setRows(rows.filter((_, i) => i !== index));
    };

    const handleChange = (index, field, value) => {
        setRows(prev => {
            const newRows = [...prev];
            newRows[index][field] = value;
            if (field === 'room' && value) fetchRawMaterials(value.value, index);
            return newRows;
        });

        // clear error for this field
        setErrors(prev => {
            const updated = [...prev];
            if (!updated[index]) updated[index] = {};
            updated[index][field] = false;
            return updated;
        });
    };

    // Validate on submit
    const handleSubmit = async () => {
        let newErrors = [];

        rows.forEach((r, i) => {
            newErrors[i] = {
                room: !r.room,
                rawMaterial: !r.rawMaterial,
                quantity: !r.quantity,
                unit: !r.unit
            };
        });

        setErrors(newErrors);

        const hasError = newErrors.some(err =>
            err.room || err.rawMaterial || err.quantity || err.unit
        );

        if (hasError) {
            // alert("Please fill all required fields.");
            return;
        }

        setLoading(true);
        try {
            const today = new Date().toISOString().split('T')[0];
            const items = rows.map(r => ({
                storagewastage_date: today,
                storagewastage_room: r.room.value,
                storagewastage_rawmaterial: r.rawMaterial.value,
                storagewastage_quantity: r.quantity,
                storagewastage_unit: r.unit.value
            }));
            const res = await axios.post(`${baseURL}/storage-wastage`, { items });

            console.log(res.data);
            onClose();
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    return (
        <Modal show={show} size="xl" onHide={onClose}>
            <Modal.Header style={{ backgroundColor: '#3ab384' }} closeButton>
                <Modal.Title className="text-white">Manage Stock Wastage</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {rows.map((row, idx) => (
                    <Row key={idx} className="mb-3 align-items-end">

                        {/* Storage Room */}
                        <Col md={3}>
                            <Form.Label style={{ color: "black" }}>
                                Storage Room <span style={{ color: "red" }}>*</span>
                            </Form.Label>

                            <Select
                                options={storageRooms}
                                value={row.room}
                                onChange={value => handleChange(idx, 'room', value)}
                                placeholder="Select Storage Room"
                                className={errors[idx]?.room }
                            />
                            {errors[idx]?.room && (
                                <small className="text-danger">This field is required</small>
                            )}
                        </Col>

                        {/* Raw Material */}
                        <Col md={3}>
                            <Form.Label style={{ color: "black" }}>
                                Raw Material <span style={{ color: "red" }}>*</span>
                            </Form.Label>

                            <Select
                                options={row.rawMaterials}
                                value={row.rawMaterial}
                                onChange={value => handleChange(idx, 'rawMaterial', value)}
                                placeholder="Select Raw Material"
                                isDisabled={!row.room}
                                className={errors[idx]?.rawMaterial }
                            />
                            {errors[idx]?.rawMaterial && (
                                <small className="text-danger">This field is required</small>
                            )}
                        </Col>

                        {/* Quantity */}
                        <Col md={2}>
                            <Form.Label style={{ color: "black" }}>
                                Quantity <span style={{ color: "red" }}>*</span>
                            </Form.Label>

                            <Form.Control
                                type="number"
                                value={row.quantity}
                                onChange={e => handleChange(idx, 'quantity', e.target.value)}
                                placeholder="Enter Quantity"
                                className={errors[idx]?.quantity }
                            />
                            {errors[idx]?.quantity && (
                                <small className="text-danger">This field is required</small>
                            )}
                        </Col>

                        {/* Unit */}
                        <Col md={2}>
                            <Form.Label style={{ color: "black" }}>
                                Unit <span style={{ color: "red" }}>*</span>
                            </Form.Label>

                            <Select
                                options={row.units}
                                value={row.unit}
                                onChange={value => handleChange(idx, 'unit', value)}
                                placeholder="Select Unit"
                                isDisabled={row.units.length === 0}
                                className={errors[idx]?.unit }
                            />
                            {errors[idx]?.unit && (
                                <small className="text-danger">This field is required</small>
                            )}
                        </Col>

                        {/* Row Remove */}
                        <Col md={2}>
                            {idx !== 0 && (
                                <Button variant="danger" onClick={() => handleRemoveRow(idx)}>
                                    <i className="fa-solid fa-trash-can fs-6 mt-1"></i>
                                </Button>
                            )}
                        </Col>
                    </Row>
                ))}

                <Button variant="secondary" className="mb-3" onClick={handleAddRow}>
                    + Add More
                </Button>

                <div className="d-flex justify-content-end gap-2">
                    <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? 'Submitting...' : 'Submit'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default StockWastageModal;
